import React, { lazy } from 'react';
import { RouteProps } from 'react-router-dom';
import {
	adminRoutes,
	componentPagesMenu,
	dashboardPagesMenu,
	demoPagesMenu,
	gettingStartedPagesMenu,
	pageLayoutTypesPagesMenu,
} from '../menu';
import Login from '../pages/presentation/auth/Login';
import ProtectedRoute from '../helpers/ProtectedRoute';

const LANDING = {
	DASHBOARD: lazy(() => import('../pages/presentation/dashboard/DashboardPage')),
	DASHBOARD_BOOKING: lazy(() => import('../pages/presentation/dashboard/DashboardBookingPage')),
	SUMMARY: lazy(() => import('../pages/presentation/SummaryPage')),
};
const SINGLE = {
	BOXED: lazy(() => import('../pages/presentation/single-pages/SingleBoxedPage')),
	FLUID: lazy(() => import('../pages/presentation/single-pages/SingleFluidPage')),
};
const LIST = {
	BOXED: lazy(() => import('../pages/presentation/demo-pages/ListBoxedPage')),
	FLUID: lazy(() => import('../pages/presentation/demo-pages/ListFluidPage')),
};
const GRID = {
	BOXED: lazy(() => import('../pages/presentation/demo-pages/GridBoxedPage')),
	FLUID: lazy(() => import('../pages/presentation/demo-pages/GridFluidPage')),
};
const EDIT = {
	MODERN: lazy(() => import('../pages/presentation/demo-pages/EditModernPage')),
	BOXED: lazy(() => import('../pages/presentation/demo-pages/EditBoxedPage')),
	FLUID: lazy(() => import('../pages/presentation/demo-pages/EditFluidPage')),
	WIZARD: lazy(() => import('../pages/presentation/demo-pages/EditWizardPage')),
	IN_CANVAS: lazy(() => import('../pages/presentation/demo-pages/EditInCanvasPage')),
	IN_MODAL: lazy(() => import('../pages/presentation/demo-pages/EditInModalPage')),
};
const PRICING = {
	PRICING_TABLE: lazy(() => import('../pages/presentation/pricing/PricingTablePage')),
};

const AUTH = {
	PAGE_404: lazy(() => import('../pages/presentation/auth/Page404')),
	UNAUTHORIZED: lazy(() => import('../pages/UnauthorizedPage')),
};

const APP = {
	PROJECT_MANAGEMENT: {
		PROJECTS_LIST: lazy(
			() => import('../pages/presentation/project-management/ProjectManagementsList'),
		),
		PROJECT: lazy(
			() => import('../pages/presentation/project-management/ProjectManagementsProject'),
		),
	},
	KNOWLEDGE: {
		GRID: lazy(() => import('../pages/presentation/knowledge/KnowledgeGridPage')),
		VIEW: lazy(() => import('../pages/presentation/knowledge/KnowledgeViewPage')),
	},
	SALES: {
		TRANSACTIONS: lazy(() => import('../pages/presentation/sales/TransActionsPage')),
		PRODUCTS: lazy(() => import('../pages/presentation/sales/SalesListPage')),
		PRODUCTS_GRID: lazy(() => import('../pages/presentation/sales/ProductsGridPage')),
		PRODUCTS_VIEW: lazy(() => import('../pages/presentation/sales/ProductViewPage')),
	},
	APPOINTMENT: {
		CALENDAR: lazy(() => import('../pages/presentation/appointment/CalendarPage')),
		EMPLOYEE_LIST: lazy(() => import('../pages/presentation/appointment/EmployeeList')),
		EMPLOYEE_VIEW: lazy(() => import('../pages/presentation/appointment/EmployeePage')),
		APPOINTMENT_LIST: lazy(() => import('../pages/presentation/appointment/AppointmentList')),
	},
	CRM: {
		CRM_DASHBOARD: lazy(() => import('../pages/presentation/crm/CrmDashboard')),
		CUSTOMERS: lazy(() => import('../pages/presentation/crm/CustomersList')),
		CUSTOMER: lazy(() => import('../pages/presentation/crm/Customer')),
	},
	CHAT: {
		WITH_LIST: lazy(() => import('../pages/presentation/chat/WithListChatPage')),
		ONLY_LIST: lazy(() => import('../pages/presentation/chat/OnlyListChatPage')),
	},
};
const PAGE_LAYOUTS = {
	HEADER_SUBHEADER: lazy(() => import('../pages/presentation/page-layouts/HeaderAndSubheader')),
	HEADER: lazy(() => import('../pages/presentation/page-layouts/OnlyHeader')),
	SUBHEADER: lazy(() => import('../pages/presentation/page-layouts/OnlySubheader')),
	CONTENT: lazy(() => import('../pages/presentation/page-layouts/OnlyContent')),
	BLANK: lazy(() => import('../pages/presentation/page-layouts/Blank')),
	ASIDE: lazy(() => import('../pages/presentation/aside-types/DefaultAsidePage')),
	MINIMIZE_ASIDE: lazy(() => import('../pages/presentation/aside-types/MinimizeAsidePage')),
};

const CONTENT = {
	CONTENTS: lazy(() => import('../pages/documentation/content/ContentListPage')),
	TYPOGRAPHY: lazy(() => import('../pages/documentation/content/TypographyPage')),
	IMAGES: lazy(() => import('../pages/documentation/content/ImagesPage')),
	TABLES: lazy(() => import('../pages/documentation/content/TablesPage')),
	FIGURES: lazy(() => import('../pages/documentation/content/FiguresPage')),
};
const FORMS_PAGE = {
	FORMS: lazy(() => import('../pages/documentation/forms/FormsListPage')),
	FORM_GROUP: lazy(() => import('../pages/documentation/forms/FormGroupPage')),
	FORM_CONTROLS: lazy(() => import('../pages/documentation/forms/FormControlsPage')),
	SELECT: lazy(() => import('../pages/documentation/forms/SelectPage')),
	CHECKS_AND_RADIO: lazy(() => import('../pages/documentation/forms/ChecksAndRadioPage')),
	RANGE: lazy(() => import('../pages/documentation/forms/RangePage')),
	INPUT_GROUP: lazy(() => import('../pages/documentation/forms/InputGroupPage')),
	VALIDATION: lazy(() => import('../pages/documentation/forms/ValidationPage')),
	WIZARD: lazy(() => import('../pages/documentation/forms/WizardPage')),
};
const GETTING_STARTED = {
	INSTALLATION: lazy(() => import('../pages/documentation/getting-started/InstallationPage')),
	DEVELOPMENT: lazy(() => import('../pages/documentation/getting-started/DevelopmentPage')),
	FOLDER: lazy(() => import('../pages/documentation/getting-started/FolderStructurePage')),
	BOOTSTRAP: lazy(() => import('../pages/documentation/getting-started/BootstrapVariablesPage')),
	PROJECT: lazy(() => import('../pages/documentation/getting-started/ProjectStructurePage')),
};
const ROUTES = {
	ROUTER: lazy(() => import('../pages/documentation/routes/RouterPage')),
};
const COMPONENTS_PAGE = {
	COMPONENTS: lazy(() => import('../pages/documentation/components/ComponentsListPage')),
	ACCORDION: lazy(() => import('../pages/documentation/components/AccordionPage')),
	ALERT: lazy(() => import('../pages/documentation/components/AlertPage')),
	BADGE: lazy(() => import('../pages/documentation/components/BadgePage')),
	BREADCRUMB: lazy(() => import('../pages/documentation/components/BreadcrumbPage')),
	BUTTON: lazy(() => import('../pages/documentation/components/ButtonPage')),
	BUTTON_GROUP: lazy(() => import('../pages/documentation/components/ButtonGroupPage')),
	CARD: lazy(() => import('../pages/documentation/components/CardPage')),
	CAROUSEL: lazy(() => import('../pages/documentation/components/CarouselPage')),
	COLLAPSE: lazy(() => import('../pages/documentation/components/CollapsePage')),
	DROPDOWN: lazy(() => import('../pages/documentation/components/DropdownsPage')),
	LIST_GROUP: lazy(() => import('../pages/documentation/components/ListGroupPage')),
	MODAL: lazy(() => import('../pages/documentation/components/ModalPage')),
	NAVS_TABS: lazy(() => import('../pages/documentation/components/NavsTabsPage')),
	OFF_CANVAS: lazy(() => import('../pages/documentation/components/OffCanvasPage')),
	PAGINATION: lazy(() => import('../pages/documentation/components/PaginationPage')),
	POPOVERS: lazy(() => import('../pages/documentation/components/PopoversPage')),
	PROGRESS: lazy(() => import('../pages/documentation/components/ProgressPage')),
	SCROLLSPY: lazy(() => import('../pages/documentation/components/ScrollspyPage')),
	SPINNER: lazy(() => import('../pages/documentation/components/SpinnersPage')),
	TABLE: lazy(() => import('../pages/documentation/components/TablePage')),
	TOASTS: lazy(() => import('../pages/documentation/components/ToastsPage')),
	TOOLTIP: lazy(() => import('../pages/documentation/components/TooltipPage')),
};
const UTILITIES = {
	UTILITIES: lazy(() => import('../pages/documentation/utilities/UtilitiesListPage')),
	API: lazy(() => import('../pages/documentation/utilities/ApiPage')),
	BACKGROUND: lazy(() => import('../pages/documentation/utilities/BackgroundPage')),
	BORDERS: lazy(() => import('../pages/documentation/utilities/BordersPage')),
	COLORS: lazy(() => import('../pages/documentation/utilities/ColorsPage')),
	DISPLAY: lazy(() => import('../pages/documentation/utilities/DisplayPage')),
	FLEX: lazy(() => import('../pages/documentation/utilities/FlexPage')),
	FLOAT: lazy(() => import('../pages/documentation/utilities/FloatPage')),
	INTERACTIONS: lazy(() => import('../pages/documentation/utilities/InteractionsPage')),
	OVERFLOW: lazy(() => import('../pages/documentation/utilities/OverflowPage')),
	POSITION: lazy(() => import('../pages/documentation/utilities/PositionPage')),
	SHADOWS: lazy(() => import('../pages/documentation/utilities/ShadowsPage')),
	SIZING: lazy(() => import('../pages/documentation/utilities/SizingPage')),
	SPACING: lazy(() => import('../pages/documentation/utilities/SpacingPage')),
	TEXT: lazy(() => import('../pages/documentation/utilities/TextPage')),
	VERTICAL_ALIGN: lazy(() => import('../pages/documentation/utilities/VerticalAlignPage')),
	VISIBILITY: lazy(() => import('../pages/documentation/utilities/VisibilityPage')),
};
const ICONS = {
	ICONS_LIST: lazy(() => import('../pages/documentation/icons/IconsListPage')),
	ICON: lazy(() => import('../pages/documentation/icons/IconPage')),
	MATERIAL: lazy(() => import('../pages/documentation/icons/MaterialPage')),
};
const CHARTS_PAGE = {
	CHART_LIST: lazy(() => import('../pages/documentation/charts/ChartsListPage')),
	GENERAL_USAGE: lazy(() => import('../pages/documentation/charts/ChartGeneralUsagePage')),
	SPARKLINE: lazy(() => import('../pages/documentation/charts/ChartSparklinePage')),
	LINE: lazy(() => import('../pages/documentation/charts/ChartLinePage')),
	AREA: lazy(() => import('../pages/documentation/charts/ChartAreaPage')),
	COLUMN: lazy(() => import('../pages/documentation/charts/ChartColumnPage')),
	BAR: lazy(() => import('../pages/documentation/charts/ChartBarPage')),
	MIXED: lazy(() => import('../pages/documentation/charts/ChartMixedPage')),
	TIMELINE: lazy(() => import('../pages/documentation/charts/ChartTimelinePage')),
	CANDLESTICK: lazy(() => import('../pages/documentation/charts/ChartCandlestickPage')),
	BOX_WHISKER: lazy(() => import('../pages/documentation/charts/ChartBoxWhiskerPage')),
	PIE_DONUT: lazy(() => import('../pages/documentation/charts/ChartPieDonutPage')),
	RADAR: lazy(() => import('../pages/documentation/charts/ChartRadarPage')),
	POLAR: lazy(() => import('../pages/documentation/charts/ChartPolarPage')),
	RADIAL_BAR: lazy(() => import('../pages/documentation/charts/ChartRadialBarPage')),
	BUBBLE: lazy(() => import('../pages/documentation/charts/ChartBubblePage')),
	SCATTER: lazy(() => import('../pages/documentation/charts/ChartScatterPage')),
	HEAT_MAP: lazy(() => import('../pages/documentation/charts/ChartHeatMapPage')),
	TREE_MAP: lazy(() => import('../pages/documentation/charts/ChartTreeMapPage')),
};
const EXTRA = {
	NOTIFICATION: lazy(() => import('../pages/documentation/extras/NotificationPage')),
	HOOKS: lazy(() => import('../pages/documentation/extras/HooksPage')),
};

// Transaction routes
const TRANSACTION_MANAGEMENT = {
	MANAGE_TRANSACTIONS: lazy(
		() => import('../custom/pages/transaction-management/ManageTransactions'),
	),
	PENDING_REVERSALS: lazy(
		() => import('../custom/pages/transaction-management/PendingReversalRequests'),
	),
};

// Settlement routes
const SETTLEMENT_MANAGEMENT = {
	SETTLEMENTS: lazy(() => import('../custom/pages/settlement-management/Settlements')),
	AGENT_TRANSACTIONS: lazy(() => import('../custom/pages/settlement-management/AgentTransactions')),
};

// Profile routes
const PROFILE = {
	ADMIN_PROFILE: lazy(() => import('../custom/pages/profile/AdminProfile')),
};

// send money routes
const SEND_MONEY = {
	CREATE_REMITTANCE: lazy(() => import('../custom/pages/send-money/CreateRemittance')),
};

// network management
const NETWORK_MANAGEMENT = {
	LOCATIONS: lazy(() => import('../custom/pages/network-managment/Locations')),
	LOCATION_DETAILS: lazy(() => import('../custom/pages/network-managment/LocationDetails')),
	MANAGE_CUSTOMERS: lazy(
		() => import('../custom/pages/system-management/customer-management/ManageCustomers'),
	),
	CUSTOMER_DETAILS: lazy(
		() => import('../custom/pages/system-management/customer-management/CustomerDetails'),
	),
	UPDATE_CUSTOMER: lazy(
		() => import('../custom/pages/system-management/customer-management/UpdateCustomer'),
	),
	PROMOTER_MANAGEMENT: lazy(() => import('../custom/pages/promoter-management/PromoterManagement')),
	VIEW_SINGLE_PROMOTER: lazy(() => import('../custom/pages/promoter-management/ViewSinglePromoter')),
	CREATE_NEW_PROMOTER: lazy(() => import('../custom/pages/promoter-management/CreatePromoter')),
	UPDATE_PROMOTER: lazy(() => import('../custom/pages/promoter-management/UpdatePromoter')),
	CUSTOMER_OTP_CODES: lazy(() => import('../custom/pages/network-management/otp-codes/CustomerOTPCodes')),
	AGENT_OTP_CODES: lazy(() => import('../custom/pages/network-management/otp-codes/AgentOTPCodes')),
	SALARY_MANAGEMENT: lazy(() => import('../custom/pages/salary-management/SalaryBatchList')),
	SALARY_BATCH_DETAILS: lazy(() => import('../custom/pages/salary-management/SalaryBatchDetails')),
	SALARY_DISBURSEMENTS: lazy(() => import('../custom/pages/salary-management/SalaryDisbursements')),
	EXPENSE_MANAGEMENT: lazy(() => import('../custom/pages/expense-management/Expenses')),
	EXPENSE_TYPES: lazy(() => import('../custom/pages/expense-management/ExpenseTypes'))
};

const COMMUNICATIONS = {
	BROADCAST: lazy(() => import('../custom/pages/communications/BroadcastNotifications')),
	HISTORY: lazy(() => import('../custom/pages/communications/NotificationHistory')),
};

const IFX = {
	BUY_AND_SELL: lazy(() => import('../custom/pages/ifx/BuyAndSell')),
	TRANSACTIONS: lazy(() => import('../custom/pages/ifx/Transactions')),
	MY_WALLET: lazy(() => import('../custom/pages/ifx/MyWallet')),
};

// system management
const SYSTEM_MANAGEMENT = {
	EXCHANGE_RATES: lazy(
		() => import('../custom/pages/system-management/exchange-rates/ExchangeRates'),
	),
	USERS: lazy(() => import('../custom/pages/system-management/user-management/Users')),
	USER_DETAILS: lazy(
		() => import('../custom/pages/system-management/user-management/UserDetails'),
	),
	SERVICE_FEES: lazy(() => import('../custom/pages/system-management/service-fees/ServiceFees')),
	SERVICE_FEE_DETAILS: lazy(
		() => import('../custom/pages/system-management/service-fees/ServiceFeeDetails'),
	),

	CREATE_SERVICE_FEE: lazy(
		() => import('../custom/pages/system-management/service-fees/CreateServiceFee'),
	),
	UPDATE_SERVICE_FEE: lazy(
		() => import('../custom/pages/system-management/service-fees/UpdateServiceFee'),
	),
	ADD_USER: lazy(() => import('../custom/pages/system-management/user-management/AddUser')),
	UPDATE_USER: lazy(() => import('../custom/pages/system-management/user-management/UpdateUser')),
	SHIFT_MANAGEMENT: lazy(
		() => import('../custom/pages/system-management/shift-management/ShiftManagement'),
	),
	ADD_NEW_CUSTOMER: lazy(
		() => import('../custom/pages/system-management/customer-management/AddCustomer'),
	),

	ADD_MERCHANT_ACCOUNT: lazy(
		() => import('../custom/pages/system-management/merchant-management/AddMerchantAccount'),
	),
	MANAGE_MERCHANT_ACCOUNTS: lazy(
		() =>
			import('../custom/pages/system-management/merchant-management/ManageMerchantsAccounts'),
	),
	MERCHANT_ACCOUNT_DETAILS: lazy(
		() => import('../custom/pages/system-management/merchant-management/MerchantDetails'),
	),

	// ROLES_AND_PERMISSIONS: lazy(
	// 	() => import('../custom/pages/system-management/roles-and-permissions/RolesAndPermissions'),
	// ),
	ROLES_AND_PERMISSIONS: lazy(
		() =>
			import(
				'../custom/pages/system-management/roles-and-permissions/RolesPermissionsDashboard'
			),
	),

	ROLES: lazy(() => import('../custom/pages/system-management/roles-and-permissions/Roles')),
	PERMISSIONS: lazy(
		() => import('../custom/pages/system-management/roles-and-permissions/Permissions'),
	),
	BULK_PERMISSION_ASSIGNMENT: lazy(
		() => import('../custom/pages/system-management/roles-and-permissions/BulkPermissionAssignment'),
	),
	VIEW_ALL_ROLES: lazy(
		() => import('../custom/pages/system-management/roles-and-permissions/ViewAllRoles'),
	),
	VIEW_ALL_PERMISSIONS: lazy(
		() => import('../custom/pages/system-management/roles-and-permissions/ViewAllPermissions'),
	),
	TICKET_SETTINGS: lazy(
		() => import('../custom/pages/system-management/ticket-settings/TicketSettings'),
	),
	SUPPORT_TICKETS: lazy(
		() => import('../custom/pages/system-management/support/SupportTickets'),
	),
	SUPPORT_TICKET_DETAILS: lazy(
		() => import('../custom/pages/system-management/support/SupportTicketDetails'),
	),
	CASHBACK_RULES: lazy(() => import('../custom/pages/system-management/cashback-rules/CashbackRules')),
	BANKS: lazy(() => import('../custom/pages/system-management/banks/Banks')),
	ADVERTS: lazy(() => import('../custom/pages/system-management/adverts/Adverts')),
};

const BUSINESS_MANAGEMENT = {
	BUSINESSES: lazy(() => import('../custom/pages/business-management/BusinessManagement')),
	// EVENT_DETAIL: lazy(
	// 	() => import('../custom/pages/system-management/events-management/EventDetails'),
	// ),
	// EVENT_CATEGORY: lazy(
	// 	() => import('../custom/pages/system-management/events-management/EventCategory'),
	// ),
	// EVENT_COMPLIANCE: lazy(
	// 	() => import('../custom/pages/system-management/events-management/EventCompliance'),
	// ),
};

// delivery-rates
const RATE_MANAGEMENT = {
	DELIVERY_RATE: lazy(() => import('../custom/pages/rates-management/DeliveryRates')),
};

// agent management
const AGENT_MANAGEMENT = {
	AGENT_OVERVIEW: lazy(() => import('../custom/pages/agent-management/AgentManagement')),
	AGENT_DETAIL: lazy(() => import('../custom/pages/agent-management/AgentDetails')),
	REGISTER_AGENT: lazy(() => import('../custom/pages/agent-management/CreateAgent')),
	UPDATE_AGENT: lazy(() => import('../custom/pages/agent-management/EditAgent')),
	AGENT_COMPANIES: lazy(() => import('../custom/pages/agent-management/AgentCompanies')),
	VIEW_AGENT_COMPANY_DETAILS: lazy(() => import('../custom/pages/agent-management/ViewAgentCompanyDetails')),
	AGENT_MAP: lazy(() => import('../custom/pages/agent-management/AgentMap')),
};

const EVENT_MANAGEMENT = {
	EVENTS: lazy(
		() => import('../custom/pages/system-management/events-management/EventManangement'),
	),
	EVENT_DETAIL: lazy(
		() => import('../custom/pages/system-management/events-management/EventDetails'),
	),
	EVENT_CATEGORY: lazy(
		() => import('../custom/pages/system-management/events-management/EventCategory'),
	),
	EVENT_COMPLIANCE: lazy(
		() => import('../custom/pages/system-management/events-management/EventCompliance'),
	),
};

const DELIVERY_MANAGEMENT = {
	DELIVERY_TRANSACTIONS: lazy(
		() => import('../custom/pages/delivery-management/DeliveryTransactions'),
	),
	MANAGE_DELIVERY_AGENTS: lazy(
		() => import('../custom/pages/delivery-management/ManageDeliveryAgents'),
	),
	DELIVERY_COMPLIANCE: lazy(
		() => import('../custom/pages/delivery-management/DeliveryCompliance'),
	),
	VIEW_DELIVERY_DETAILS: lazy(
		() => import('../custom/pages/delivery-management/ViewDeliveryTransaction'),
	),
	VIEW_AGENT_DETAILS: lazy(() => import('../custom/pages/delivery-management/ViewDeliveryAgent')),
	VIEW_DELIVERY_AGENT_COMPLIANCE: lazy(
		() => import('../custom/pages/delivery-management/ViewDeliveryCompliance'),
	),
};

// products
const PRODUCT_MANAGEMENT = {
	PRODUCTS: lazy(() => import('../custom/pages/system-management/products/Products')),
	PRODUCT_DETAILS: lazy(
		() => import('../custom/pages/system-management/products/ProductDetails'),
	),
};

//Merchants
const MERCHANT_MANAGEMENT = {
	MERCHANTS: lazy(() => import('../custom/pages/merchant-management/MerchantManagement')),
	MERCHANT_DETAILS: lazy(() => import('../custom/pages/merchant-management/MerchantDetails')),
	BUSINESS_DETAILS: lazy(
		() => import('../custom/pages/merchant-management/MerchantBusinessDetail'),
	),
};

const FLOAT_MANAGEMENT = {
	// FLOATS: lazy(() => import('../custom/pages/float-management/FloatManagement')),
	FLOATREQUESTS: lazy(() => import('../custom/pages/float-management/ManageFloatRequests')),
	// AGENTDETAIL: lazy(() => import('../custom/pages/float-management/AgentDetail')),
	// TRANSACTIONS: lazy(() => import('../custom/pages/float-management/FloatTransactions')),
};

const REFUND_REQUEST = {
	REFUNDREQUEST: lazy(() => import('../custom/pages/refund-request/RefundRequests')),
	REFUNDREQUEST_DETAILS: lazy(
		() => import('../custom/pages/refund-request/RefundRequestDetails'),
	),
};

const MANAGE_TRANSACTIONS = {
	MANAGETRANSACTIONS: lazy(() => import('../custom/pages/Transfer/ManagePayment')),
};

// compliance management
const COMPLIANCE_MANAGEMENT = {
	COMPLIANCES: lazy(() => import('../custom/pages/compliance-management/Compliances')),
	CUSTOMER_COMPLIANCE: lazy(
		() => import('../custom/pages/compliance-management/CustomerCompliance'),
	),
	MERCHANT_COMPLIANCE: lazy(
		() => import('../custom/pages/compliance-management/MerchantCompliance'),
	),
	BUSINESS_COMPLIANCE: lazy(
		() => import('../custom/pages/compliance-management/BusinessCompliance'),
	),
	AGENT_COMPLIANCE: lazy(
		() => import('../custom/pages/compliance-management/AgentCompliance'),
	),
	AGENT_KYC_DETAILS: lazy(
		() => import('../custom/pages/compliance-management/AgentKYCDetails'),
	),
	VIEW_MERCHANT_COMPLIANCE: lazy(
		() => import('../custom/pages/compliance-management/ViewMerchantCompliance'),
	),
	VIEW_BUSINESS_COMPLIANCE: lazy(
		() => import('../custom/pages/compliance-management/ViewBusinessCompliance'),
	),
	VIEW_CUSTOMER_COMPLIANCE: lazy(
		() => import('../custom/pages/compliance-management/ViewCustomerCompliance'),
	),
};
const REPORTS = {
	REPORTS: lazy(() => import('../custom/pages/reports/Reports')),
	COMPLIANCE_REPORTS: lazy(() => import('../custom/pages/reports/ComplianceReports')),
}

// financial management
const FINANCIAL_MANAGEMENT = {
	CHART_OF_ACCOUNTS: lazy(() => import('../custom/pages/financial-management/ChartOfAccounts')),
	ACCOUNT_DETAILS: lazy(() => import('../custom/pages/financial-management/AccountDetailsPage')),
	VAULT_MANAGEMENT: lazy(() => import('../custom/pages/vault-management/VaultManagement')),
	LIQUIDITY_BREAKDOWN: lazy(() => import('../custom/pages/financial-management/LiquidityBreakdown')),
}

//new sidebar route
const PAYBILLS = {
	COMPLIANCES: lazy(() => import('../custom/pages/compliance-management/Compliances')),
};

const TRANSFER = {
	MANAGE_PAYMENT: lazy(() => import('../custom/pages/Transfer/ManagePayment')),
	TRANSFER_MANAGEMENT: lazy(() => import('../custom/pages/transfer-management/TransferManagement')),
};

const UNAUTHORIZED = {
	UNAUTHORIZED_PAGE: lazy(() => import('../custom/pages/unauthorized/UnauthorizedPage')),
};

const presentation: RouteProps[] = [
	/**
	 * Landing
	 */
	{
		path: dashboardPagesMenu.dashboard.path,
		element: <LANDING.DASHBOARD />,
	},
	{
		path: dashboardPagesMenu.dashboardBooking.path,
		element: <LANDING.DASHBOARD_BOOKING />,
	},
	{
		path: dashboardPagesMenu.summary.path,
		element: <LANDING.SUMMARY />,
	},

	/** ************************************************** */

	/**
	 * Pages
	 */

	/**
	 * Single Pages
	 */
	{
		path: demoPagesMenu.singlePages.subMenu.boxedSingle.path,
		element: <SINGLE.BOXED />,
	},
	{
		path: demoPagesMenu.singlePages.subMenu.fluidSingle.path,
		element: <SINGLE.FLUID />,
	},

	/**
	 * List
	 */
	{
		path: demoPagesMenu.listPages.subMenu.listBoxed.path,
		element: <LIST.BOXED />,
	},
	{
		path: demoPagesMenu.listPages.subMenu.listFluid.path,
		element: <LIST.FLUID />,
	},

	/**
	 * Grid
	 */
	{
		path: demoPagesMenu.gridPages.subMenu.gridBoxed.path,
		element: <GRID.BOXED />,
	},
	{
		path: demoPagesMenu.gridPages.subMenu.gridFluid.path,
		element: <GRID.FLUID />,
	},

	/**
	 * Edit
	 */
	{
		path: demoPagesMenu.editPages.subMenu.editModern.path,
		element: <EDIT.MODERN />,
	},
	{
		path: demoPagesMenu.editPages.subMenu.editBoxed.path,
		element: <EDIT.BOXED />,
	},
	{
		path: demoPagesMenu.editPages.subMenu.editFluid.path,
		element: <EDIT.FLUID />,
	},
	{
		path: demoPagesMenu.editPages.subMenu.editWizard.path,
		element: <EDIT.WIZARD />,
	},
	{
		path: demoPagesMenu.editPages.subMenu.editInCanvas.path,
		element: <EDIT.IN_CANVAS />,
	},
	{
		path: demoPagesMenu.editPages.subMenu.editInModal.path,
		element: <EDIT.IN_MODAL />,
	},

	{
		path: demoPagesMenu.pricingTable.path,
		element: <PRICING.PRICING_TABLE />,
	},

	/**
	 * END - Pages
	 */

	/**
	 * Auth Page
	 */
	{
		path: demoPagesMenu.page404.path,
		element: <AUTH.PAGE_404 />,
	},
	{
		path: '/unauthorized',
		element: <AUTH.UNAUTHORIZED />,
	},
	{
		path: demoPagesMenu.login.path,
		element: <Login />,
	},

	/**
	 * App
	 */

	/**
	 * App > Project Management
	 */
	{
		path: demoPagesMenu.projectManagement.subMenu.list.path,
		element: <APP.PROJECT_MANAGEMENT.PROJECTS_LIST />,
	},
	{
		path: `${demoPagesMenu.projectManagement.subMenu.itemID.path}/:id`,
		element: <APP.PROJECT_MANAGEMENT.PROJECT />,
	},

	/**
	 * App > Knowledge
	 */
	{
		path: demoPagesMenu.knowledge.subMenu.grid.path,
		element: <APP.KNOWLEDGE.GRID />,
	},
	{
		path: `${demoPagesMenu.knowledge.subMenu.itemID.path}/:id`,
		element: <APP.KNOWLEDGE.VIEW />,
	},

	/**
	 * App > Sales
	 */
	{
		path: demoPagesMenu.sales.subMenu.transactions.path,
		element: <APP.SALES.TRANSACTIONS />,
	},
	{
		path: demoPagesMenu.sales.subMenu.salesList.path,
		element: <APP.SALES.PRODUCTS />,
	},
	{
		path: demoPagesMenu.sales.subMenu.productsGrid.path,
		element: <APP.SALES.PRODUCTS_GRID />,
	},
	{
		path: `${demoPagesMenu.sales.subMenu.productID.path}/:id`,
		element: <APP.SALES.PRODUCTS_VIEW />,
	},

	/**
	 * App > Appointment
	 */
	{
		path: demoPagesMenu.appointment.subMenu.calendar.path,
		element: <APP.APPOINTMENT.CALENDAR />,
	},
	{
		path: demoPagesMenu.appointment.subMenu.employeeList.path,
		element: <APP.APPOINTMENT.EMPLOYEE_LIST />,
	},
	{
		path: `${demoPagesMenu.appointment.subMenu.employeeID.path}/:id`,
		element: <APP.APPOINTMENT.EMPLOYEE_VIEW />,
	},
	{
		path: demoPagesMenu.appointment.subMenu.appointmentList.path,
		element: <APP.APPOINTMENT.APPOINTMENT_LIST />,
	},

	/**
	 * App > CRM
	 */
	{
		path: demoPagesMenu.crm.subMenu.dashboard.path,
		element: <APP.CRM.CRM_DASHBOARD />,
	},
	{
		path: demoPagesMenu.crm.subMenu.customersList.path,
		element: <APP.CRM.CUSTOMERS />,
	},
	{
		path: `${demoPagesMenu.crm.subMenu.customerID.path}/:id`,
		element: <APP.CRM.CUSTOMER />,
	},

	/**
	 * App > Chat
	 */
	{
		path: demoPagesMenu.chat.subMenu.withListChat.path,
		element: <APP.CHAT.WITH_LIST />,
	},
	{
		path: demoPagesMenu.chat.subMenu.onlyListChat.path,
		element: <APP.CHAT.ONLY_LIST />,
	},

	/**
	 * END - App
	 */

	/** ************************************************** */

	/**
	 * Page Layout Types
	 */
	{
		path: pageLayoutTypesPagesMenu.blank.path,
		element: <PAGE_LAYOUTS.BLANK />,
	},
	{
		path: pageLayoutTypesPagesMenu.pageLayout.subMenu.headerAndSubheader.path,
		element: <PAGE_LAYOUTS.HEADER_SUBHEADER />,
	},
	{
		path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlyHeader.path,
		element: <PAGE_LAYOUTS.HEADER />,
	},
	{
		path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlySubheader.path,
		element: <PAGE_LAYOUTS.SUBHEADER />,
	},
	{
		path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlyContent.path,
		element: <PAGE_LAYOUTS.CONTENT />,
	},
	{
		path: pageLayoutTypesPagesMenu.asideTypes.subMenu.defaultAside.path,
		element: <PAGE_LAYOUTS.ASIDE />,
	},
	{
		path: pageLayoutTypesPagesMenu.asideTypes.subMenu.minimizeAside.path,
		element: <PAGE_LAYOUTS.MINIMIZE_ASIDE />,
	},
];
const documentation: RouteProps[] = [
	/**
	 * Getting Started
	 */
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.installation.path,
		element: <GETTING_STARTED.INSTALLATION />,
	},
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.dev.path,
		element: <GETTING_STARTED.DEVELOPMENT />,
	},
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.folderStructure.path,
		element: <GETTING_STARTED.FOLDER />,
	},
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.bootstrapVariables.path,
		element: <GETTING_STARTED.BOOTSTRAP />,
	},
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.projectStructure.path,
		element: <GETTING_STARTED.PROJECT />,
	},
	/**
	 * Routes
	 */
	{
		path: gettingStartedPagesMenu.routes.subMenu.router.path,
		element: <ROUTES.ROUTER />,
	},
	/**
	 * Bootstrap
	 */

	/**
	 * Content
	 */
	{
		path: componentPagesMenu.content.path,
		element: <CONTENT.CONTENTS />,
	},
	{
		path: componentPagesMenu.content.subMenu.typography.path,
		element: <CONTENT.TYPOGRAPHY />,
	},
	{
		path: componentPagesMenu.content.subMenu.images.path,
		element: <CONTENT.IMAGES />,
	},
	{
		path: componentPagesMenu.content.subMenu.tables.path,
		element: <CONTENT.TABLES />,
	},
	{
		path: componentPagesMenu.content.subMenu.figures.path,
		element: <CONTENT.FIGURES />,
	},

	/**
	 * Forms
	 */
	{
		path: componentPagesMenu.forms.path,
		element: <FORMS_PAGE.FORMS />,
	},
	{
		path: componentPagesMenu.forms.subMenu.formGroup.path,
		element: <FORMS_PAGE.FORM_GROUP />,
	},
	{
		path: componentPagesMenu.forms.subMenu.formControl.path,
		element: <FORMS_PAGE.FORM_CONTROLS />,
	},
	{
		path: componentPagesMenu.forms.subMenu.select.path,
		element: <FORMS_PAGE.SELECT />,
	},
	{
		path: componentPagesMenu.forms.subMenu.checksAndRadio.path,
		element: <FORMS_PAGE.CHECKS_AND_RADIO />,
	},
	{
		path: componentPagesMenu.forms.subMenu.range.path,
		element: <FORMS_PAGE.RANGE />,
	},
	{
		path: componentPagesMenu.forms.subMenu.inputGroup.path,
		element: <FORMS_PAGE.INPUT_GROUP />,
	},
	{
		path: componentPagesMenu.forms.subMenu.validation.path,
		element: <FORMS_PAGE.VALIDATION />,
	},
	{
		path: componentPagesMenu.forms.subMenu.wizard.path,
		element: <FORMS_PAGE.WIZARD />,
	},

	/**
	 * Components
	 */
	{
		path: componentPagesMenu.components.path,
		element: <COMPONENTS_PAGE.COMPONENTS />,
	},
	{
		path: componentPagesMenu.components.subMenu.tooltip.path,
		element: <COMPONENTS_PAGE.TOOLTIP />,
	},
	{
		path: componentPagesMenu.components.subMenu.toasts.path,
		element: <COMPONENTS_PAGE.TOASTS />,
	},
	{
		path: componentPagesMenu.components.subMenu.scrollspy.path,
		element: <COMPONENTS_PAGE.SCROLLSPY />,
	},
	{
		path: componentPagesMenu.components.subMenu.carousel.path,
		element: <COMPONENTS_PAGE.CAROUSEL />,
	},
	{
		path: componentPagesMenu.components.subMenu.spinners.path,
		element: <COMPONENTS_PAGE.SPINNER />,
	},
	{
		path: componentPagesMenu.components.subMenu.listGroup.path,
		element: <COMPONENTS_PAGE.LIST_GROUP />,
	},
	{
		path: componentPagesMenu.components.subMenu.breadcrumb.path,
		element: <COMPONENTS_PAGE.BREADCRUMB />,
	},
	{
		path: componentPagesMenu.components.subMenu.collapse.path,
		element: <COMPONENTS_PAGE.COLLAPSE />,
	},
	{
		path: componentPagesMenu.components.subMenu.pagination.path,
		element: <COMPONENTS_PAGE.PAGINATION />,
	},
	{
		path: componentPagesMenu.components.subMenu.progress.path,
		element: <COMPONENTS_PAGE.PROGRESS />,
	},
	{
		path: componentPagesMenu.components.subMenu.card.path,
		element: <COMPONENTS_PAGE.CARD />,
	},
	{
		path: componentPagesMenu.components.subMenu.button.path,
		element: <COMPONENTS_PAGE.BUTTON />,
	},
	{
		path: componentPagesMenu.components.subMenu.buttonGroup.path,
		element: <COMPONENTS_PAGE.BUTTON_GROUP />,
	},
	{
		path: componentPagesMenu.components.subMenu.alert.path,
		element: <COMPONENTS_PAGE.ALERT />,
	},
	{
		path: componentPagesMenu.components.subMenu.badge.path,
		element: <COMPONENTS_PAGE.BADGE />,
	},
	{
		path: componentPagesMenu.components.subMenu.popovers.path,
		element: <COMPONENTS_PAGE.POPOVERS />,
	},
	{
		path: componentPagesMenu.components.subMenu.dropdowns.path,
		element: <COMPONENTS_PAGE.DROPDOWN />,
	},
	{
		path: componentPagesMenu.components.subMenu.accordion.path,
		element: <COMPONENTS_PAGE.ACCORDION />,
	},
	{
		path: componentPagesMenu.components.subMenu.modal.path,
		element: <COMPONENTS_PAGE.MODAL />,
	},
	{
		path: componentPagesMenu.components.subMenu.navsTabs.path,
		element: <COMPONENTS_PAGE.NAVS_TABS />,
	},
	{
		path: componentPagesMenu.components.subMenu.offcanvas.path,
		element: <COMPONENTS_PAGE.OFF_CANVAS />,
	},
	{
		path: componentPagesMenu.components.subMenu.table.path,
		element: <COMPONENTS_PAGE.TABLE />,
	},

	/**
	 * Utilities
	 */
	{
		path: componentPagesMenu.utilities.path,
		element: <UTILITIES.UTILITIES />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.api.path,
		element: <UTILITIES.API />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.background.path,
		element: <UTILITIES.BACKGROUND />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.borders.path,
		element: <UTILITIES.BORDERS />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.colors.path,
		element: <UTILITIES.COLORS />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.display.path,
		element: <UTILITIES.DISPLAY />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.flex.path,
		element: <UTILITIES.FLEX />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.float.path,
		element: <UTILITIES.FLOAT />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.interactions.path,
		element: <UTILITIES.INTERACTIONS />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.overflow.path,
		element: <UTILITIES.OVERFLOW />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.position.path,
		element: <UTILITIES.POSITION />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.shadows.path,
		element: <UTILITIES.SHADOWS />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.sizing.path,
		element: <UTILITIES.SIZING />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.spacing.path,
		element: <UTILITIES.SPACING />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.text.path,
		element: <UTILITIES.TEXT />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.verticalAlign.path,
		element: <UTILITIES.VERTICAL_ALIGN />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.visibility.path,
		element: <UTILITIES.VISIBILITY />,
	},

	/**
	 * Extra
	 */

	/**
	 * Icons
	 */
	{
		path: componentPagesMenu.icons.path,
		element: <ICONS.ICONS_LIST />,
	},
	{
		path: componentPagesMenu.icons.subMenu.icon.path,
		element: <ICONS.ICON />,
	},
	{
		path: componentPagesMenu.icons.subMenu.material.path,
		element: <ICONS.MATERIAL />,
	},

	/**
	 * Charts
	 */
	{
		path: componentPagesMenu.charts.path,
		element: <CHARTS_PAGE.CHART_LIST />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsUsage.path,
		element: <CHARTS_PAGE.GENERAL_USAGE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsSparkline.path,
		element: <CHARTS_PAGE.SPARKLINE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsLine.path,
		element: <CHARTS_PAGE.LINE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsArea.path,
		element: <CHARTS_PAGE.AREA />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsColumn.path,
		element: <CHARTS_PAGE.COLUMN />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsBar.path,
		element: <CHARTS_PAGE.BAR />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsMixed.path,
		element: <CHARTS_PAGE.MIXED />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsTimeline.path,
		element: <CHARTS_PAGE.TIMELINE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsCandleStick.path,
		element: <CHARTS_PAGE.CANDLESTICK />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsBoxWhisker.path,
		element: <CHARTS_PAGE.BOX_WHISKER />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsPieDonut.path,
		element: <CHARTS_PAGE.PIE_DONUT />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsRadar.path,
		element: <CHARTS_PAGE.RADAR />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsPolar.path,
		element: <CHARTS_PAGE.POLAR />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsRadialBar.path,
		element: <CHARTS_PAGE.RADIAL_BAR />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsBubble.path,
		element: <CHARTS_PAGE.BUBBLE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsScatter.path,
		element: <CHARTS_PAGE.SCATTER />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsHeatMap.path,
		element: <CHARTS_PAGE.HEAT_MAP />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsTreeMap.path,
		element: <CHARTS_PAGE.TREE_MAP />,
	},

	{
		path: componentPagesMenu.notification.path,
		element: <EXTRA.NOTIFICATION />,
	},
	{
		path: componentPagesMenu.hooks.path,
		element: <EXTRA.HOOKS />,
	},

	{
		path: '/network-management/customers/create',
		element: <SYSTEM_MANAGEMENT.ADD_NEW_CUSTOMER />,
	},
	{
		path: adminRoutes.userNetworkManagement.subMenu.manageCustomers.path,
		element: (
			<ProtectedRoute requiredPermission='network-management.customer-management'>
				<NETWORK_MANAGEMENT.MANAGE_CUSTOMERS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/merchant-accounts/add',
		element: <SYSTEM_MANAGEMENT.ADD_MERCHANT_ACCOUNT />,
	},
	{
		path: '/network-management/merchant-accounts/:id',
		element: <SYSTEM_MANAGEMENT.MERCHANT_ACCOUNT_DETAILS />,
	},
	// {
	// 	path: adminRoutes.systemManagement.subMenu.merchantAccounts.path,
	// 	element: <SYSTEM_MANAGEMENT.MANAGE_MERCHANT_ACCOUNTS />,
	// },
	{
		path: '/network-management/customers/:id',
		element: (
			<ProtectedRoute requiredPermission='network-management.customer-management'>
				<NETWORK_MANAGEMENT.CUSTOMER_DETAILS />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/customers/update/:customerName/:customerId',
		element: (
			<ProtectedRoute requiredPermission='network-management.edit-customer'>
				<NETWORK_MANAGEMENT.UPDATE_CUSTOMER />
			</ProtectedRoute>
		),
	},

	{
		path: adminRoutes.userNetworkManagement.subMenu.manageLocations.path,
		element: (
			<ProtectedRoute requiredPermission='network-management.manage-locations'>
				<NETWORK_MANAGEMENT.LOCATIONS />,
			</ProtectedRoute>
		),
	},

	{
		path: '/network-management/locations/:locationId',
		element: (
			<ProtectedRoute requiredPermission='network-management.manage-locations'>
				<NETWORK_MANAGEMENT.LOCATION_DETAILS />,
			</ProtectedRoute>
		),
	},

	{
		path: '/system-administration/business-settings',
		element: (
			<ProtectedRoute requiredPermission='system-administration.business-settings'>
				<BUSINESS_MANAGEMENT.BUSINESSES />,
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/salary-management',
		element: <NETWORK_MANAGEMENT.SALARY_MANAGEMENT />
	},
	{
		path: '/financial-management/salary-management/batch/:id',
		element: <NETWORK_MANAGEMENT.SALARY_BATCH_DETAILS />
	},
	{
		path: '/financial-management/salary-management/disbursements',
		element: <NETWORK_MANAGEMENT.SALARY_DISBURSEMENTS />
	},
	{
		path: '/financial-management/expenses',
		element: <NETWORK_MANAGEMENT.EXPENSE_MANAGEMENT />
	},

	{
		path: adminRoutes.systemAdministration.subMenu.exchangeRates.path,
		element: (
			<ProtectedRoute requiredPermission='system-administration.exchange-rates' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.EXCHANGE_RATES />,
			</ProtectedRoute>
		),
	},
	{
		path: adminRoutes.userNetworkManagement.subMenu.users.path,
		element: (
			<ProtectedRoute requiredPermission='network-management.user-management' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.USERS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/users/:id',
		element: (
			<ProtectedRoute requiredPermission='network-management.user-management' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.USER_DETAILS />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/users/create',
		element: (
			<ProtectedRoute requiredPermission='network-management.create-user' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.ADD_USER />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/users/update/:id',
		element: (
			<ProtectedRoute requiredPermission='network-management.edit-user' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.UPDATE_USER />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/roles-permissions',
		element: (
			<ProtectedRoute requiredPermission='network-management.roles--permissions' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.ROLES_AND_PERMISSIONS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/roles-permissions/roles',
		element: (
			<ProtectedRoute requiredPermission='network-management.roles--permissions' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.ROLES />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/roles-permissions/permissions',
		element: (
			<ProtectedRoute requiredPermission='network-management.roles--permissions' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.PERMISSIONS />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/roles-permissions/bulk-assignment',
		element: (
			<ProtectedRoute requiredPermission='network-management.roles--permissions' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.BULK_PERMISSION_ASSIGNMENT />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/roles-permissions/all-roles',
		element: (
			<ProtectedRoute requiredPermission='network-management.roles--permissions' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.VIEW_ALL_ROLES />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/roles-permissions/all-permissions',
		element: (
			<ProtectedRoute requiredPermission='network-management.roles--permissions' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.VIEW_ALL_PERMISSIONS />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/roles-permissions',
		element: (
			<ProtectedRoute requiredPermission='network-management.roles--permissions' parentPermission='network-management'>
				<SYSTEM_MANAGEMENT.ROLES_AND_PERMISSIONS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/banks',
		element: (
			<ProtectedRoute requiredPermission='financial-management.banks' parentPermission='financial-management'>
				<SYSTEM_MANAGEMENT.BANKS />
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/chart-of-accounts',
		element: (
			<ProtectedRoute requiredPermission='financial-management.chart-of-accounts' parentPermission='financial-management'>
				<FINANCIAL_MANAGEMENT.CHART_OF_ACCOUNTS />
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/chart-of-accounts/accounts/:id',
		element: (
			<ProtectedRoute requiredPermission='financial-management.chart-of-accounts' parentPermission='financial-management'>
				<FINANCIAL_MANAGEMENT.ACCOUNT_DETAILS />
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/vault-management',
		element: (
			<ProtectedRoute requiredPermission='financial-management.vault-management' parentPermission='financial-management'>
				<FINANCIAL_MANAGEMENT.VAULT_MANAGEMENT />
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/cashback-rules',
		element: (
			<ProtectedRoute requiredPermission='financial-management.cashback-rules' parentPermission='financial-management'>
				<SYSTEM_MANAGEMENT.CASHBACK_RULES />
			</ProtectedRoute>
		),
	},

	//Products
	{
		path: '/product-management/products',
		element: <PRODUCT_MANAGEMENT.PRODUCTS />,
	},
	{
		path: '/product-management/product/:id',
		element: <PRODUCT_MANAGEMENT.PRODUCT_DETAILS />,
	},

	// Floats
	// {
	// 	path: adminRoutes.FloatManagement.subMenu.Overview.path,
	// 	element: <FLOAT_MANAGEMENT.FLOATS />,
	// },
	{
		path: '/financial-management/float-requests',
		element: (
			<ProtectedRoute requiredPermission='financial-management.float-requests'>
				<FLOAT_MANAGEMENT.FLOATREQUESTS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/liquidity-breakdown',
		element:

			<FINANCIAL_MANAGEMENT.LIQUIDITY_BREAKDOWN />,


	},
	{
		path: '/financial-management/refund-requests',
		element: (
			<ProtectedRoute requiredPermission='financial-management.refund-requests'>
				<REFUND_REQUEST.REFUNDREQUEST />,
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/refund-requests/details/:id',
		element: <REFUND_REQUEST.REFUNDREQUEST_DETAILS />,
	},

	{
		path: '/operations-services/manage-transactions',
		element: (
			<ProtectedRoute requiredPermission='operations--services.manage-transactions'>
				<TRANSACTION_MANAGEMENT.MANAGE_TRANSACTIONS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/operations-services/pending-reversals',
		element: (
			<ProtectedRoute requiredPermission='operations--services.manage-transactions'>
				<TRANSACTION_MANAGEMENT.PENDING_REVERSALS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/profile',
		element: <PROFILE.ADMIN_PROFILE />,
	},
	{
		path: '/temp/manage-transactions',
		element: <MANAGE_TRANSACTIONS.MANAGETRANSACTIONS />,
	},

	// {
	// 	path: adminRoutes.FloatManagement.subMenu.Transactions.path,
	// 	element: <FLOAT_MANAGEMENT.TRANSACTIONS />,
	// },
	// {
	// 	path: `${adminRoutes.FloatManagement.path}/agents/:id`,
	// 	element: <FLOAT_MANAGEMENT.AGENTDETAIL />,
	// },
	{
		path: '/network-management/agents',
		element: (
			<ProtectedRoute requiredPermission='network-management.agent-management'>
				<AGENT_MANAGEMENT.AGENT_OVERVIEW />,
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/agents/:id',
		element: (
			<ProtectedRoute requiredPermission='network-management.agent-management'>
				<AGENT_MANAGEMENT.AGENT_DETAIL />
			</ProtectedRoute>
		),
	},

	{
		path: '/network-management/agents/register-agent',
		element: <AGENT_MANAGEMENT.REGISTER_AGENT />,
	},

	{
		path: '/network-management/agents/update/:agentName/:agentId',
		element: (
			<ProtectedRoute requiredPermission='network-management.edit-agent'>
				<AGENT_MANAGEMENT.UPDATE_AGENT />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/agent-companies',
		element: <AGENT_MANAGEMENT.AGENT_COMPANIES />,
	},
	{
		path: '/network-management/agent-companies/:id',
		element: <AGENT_MANAGEMENT.VIEW_AGENT_COMPANY_DETAILS />,
	},
	{
		path: '/network-management/agent-map',
		element: <AGENT_MANAGEMENT.AGENT_MAP />,
	},


	//Merchants
	{
		path: '/network-management/merchants',
		element: (
			<ProtectedRoute requiredPermission='network-management.merchant-management'>
				<MERCHANT_MANAGEMENT.MERCHANTS />,
			</ProtectedRoute>
		),
	},

	{
		path: '/network-management/merchants/merchant/:id',
		element: (
			<ProtectedRoute requiredPermission='network-management.merchant-management'>
				<MERCHANT_MANAGEMENT.MERCHANT_DETAILS />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/merchants/business/:id',
		element: <MERCHANT_MANAGEMENT.BUSINESS_DETAILS />,
	},
	{
		path: '/operations-services/event-management',
		element: (
			<ProtectedRoute requiredPermission='operations--services.event-management'>
				<EVENT_MANAGEMENT.EVENTS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/operations-services/event-management/event/:id',
		element: <EVENT_MANAGEMENT.EVENT_DETAIL />,
	},

	{
		path: '/operations-services/delivery-services/transactions',
		element: (
			<ProtectedRoute requiredPermission='operations--services.delivery-transactions' parentPermission='operations--services.delivery-services'>
				<DELIVERY_MANAGEMENT.DELIVERY_TRANSACTIONS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/operations-services/delivery-services/agents',
		element: (
			<ProtectedRoute requiredPermission='operations--services.manage-delivery-agents' parentPermission='operations--services.delivery-services'>
				<DELIVERY_MANAGEMENT.MANAGE_DELIVERY_AGENTS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/operations-services/delivery-services/transaction/:id',
		element: (
			<ProtectedRoute requiredPermission='operations--services.delivery-transactions' parentPermission='operations--services.delivery-services'>
				<DELIVERY_MANAGEMENT.VIEW_DELIVERY_DETAILS />
			</ProtectedRoute>
		),
	},
	{
		path: '/operations-services/delivery-services/agent/:id',
		element: (
			<ProtectedRoute requiredPermission='operations--services.manage-delivery-agents' parentPermission='operations--services.delivery-services'>
				<DELIVERY_MANAGEMENT.VIEW_AGENT_DETAILS />
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/delivery-compliance/kyc/:id',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.delivery-compliance' parentPermission='compliance--security'>
				<DELIVERY_MANAGEMENT.VIEW_DELIVERY_AGENT_COMPLIANCE />
			</ProtectedRoute>
		),
	},
	{
		path: '/operations-services/delivery-rates',
		element: (
			<ProtectedRoute requiredPermission='operations--services.delivery-rates'>
				<RATE_MANAGEMENT.DELIVERY_RATE />,
			</ProtectedRoute>
		),
	},
	{
		path: '/communications/advertisements',
		element: (
			<ProtectedRoute requiredPermission='communications.advertisements'>
				<SYSTEM_MANAGEMENT.ADVERTS />
			</ProtectedRoute>
		),
	},
	{
		path: '/network-management/promoters',
		element: <NETWORK_MANAGEMENT.PROMOTER_MANAGEMENT />,
	},
	{
		path: '/network-management/promoters/:id',
		element: <NETWORK_MANAGEMENT.VIEW_SINGLE_PROMOTER />
	},
	{
		path: '/network-management/promoters/create',
		element: <NETWORK_MANAGEMENT.CREATE_NEW_PROMOTER />
	},
	{
		path: '/network-management/promoters/update/:id',
		element: <NETWORK_MANAGEMENT.UPDATE_PROMOTER />
	},
	{
		path: adminRoutes.systemAdministration.subMenu.serviceFees.path,
		element: (
			<ProtectedRoute requiredPermission='system-administration.service-fee' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.SERVICE_FEES />
			</ProtectedRoute>
		),
	},
	{
		path: '/system-administration/service-fees/:id',
		element: (
			<ProtectedRoute requiredPermission='system-administration.service-fee' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.SERVICE_FEES />
			</ProtectedRoute>
		),
	},
	{
		path: '/system-administration/service-fees/create',
		element: (
			<ProtectedRoute requiredPermission='system-administration.service-fee' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.CREATE_SERVICE_FEE />
			</ProtectedRoute>
		),
	},
	{
		path: '/system-administration/service-fees/update',
		element: (
			<ProtectedRoute requiredPermission='system-administration.service-fee' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.UPDATE_SERVICE_FEE />
			</ProtectedRoute>
		),
	},

	{
		path: '/system-administration/ticket-settings',
		element: (
			<ProtectedRoute requiredPermission='system-administration.event-ticket-settings' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.TICKET_SETTINGS />
			</ProtectedRoute>
		),
	},
	{
		path: '/system-administration/support-tickets',
		element: (
			<ProtectedRoute requiredPermission='system-administration.support-tickets' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.SUPPORT_TICKETS />
			</ProtectedRoute>
		),
	},
	{
		path: '/system-administration/support-tickets/:ticketId',
		element: (
			<ProtectedRoute requiredPermission='system-administration.support-tickets' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.SUPPORT_TICKET_DETAILS />
			</ProtectedRoute>
		),
	},
	{
		path: '/system-administration/shift-management',
		element: (
			<ProtectedRoute requiredPermission='system-administration.shift-management' parentPermission='system-administration'>
				<SYSTEM_MANAGEMENT.SHIFT_MANAGEMENT />,
			</ProtectedRoute>
		),
	},
	{
		path: '/system-administration/expense-types',
		element: <NETWORK_MANAGEMENT.EXPENSE_TYPES />
	},

	{
		path: '/communications/broadcast',
		element: (
			<ProtectedRoute requiredPermission='communications.broadcast-notifications'>
				<COMMUNICATIONS.BROADCAST />
			</ProtectedRoute>
		),
	},
	{
		path: '/communications/history',
		element: (
			<ProtectedRoute requiredPermission='communications.notification-history'>
				<COMMUNICATIONS.HISTORY />
			</ProtectedRoute>
		),
	},

	// Complaince Management Routes
	{
		path: 'compliance-security/compliances',
		element: (
			<ProtectedRoute requiredPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.COMPLIANCES />,
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/customer-compliance',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.customer-compliance' parentPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.CUSTOMER_COMPLIANCE />,
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/merchant-compliance',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.merchant-compliance' parentPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.MERCHANT_COMPLIANCE />,
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/business-compliance',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.business-compliance' parentPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.BUSINESS_COMPLIANCE />,
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/agent-compliance',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.agent-compliance' parentPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.AGENT_COMPLIANCE />,
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/agent-compliance/kyc/:agentId',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.agent-compliance' parentPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.AGENT_KYC_DETAILS />
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/merchant-compliance/kyc/:id',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.merchant-compliance' parentPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.VIEW_MERCHANT_COMPLIANCE />
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/business-compliance/kyc/:id',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.business-compliance' parentPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.VIEW_BUSINESS_COMPLIANCE />
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/customer-compliance/kyc/:id',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.customer-compliance' parentPermission='compliance--security'>
				<COMPLIANCE_MANAGEMENT.VIEW_CUSTOMER_COMPLIANCE />
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/delivery-compliance',
		element: (
			<ProtectedRoute requiredPermission='compliance--security.delivery-compliance' parentPermission='operations--services.delivery-services'>
				<DELIVERY_MANAGEMENT.DELIVERY_COMPLIANCE />,
			</ProtectedRoute>
		),
	},
	{
		path: '/compliance-security/otp-codes/customer',
		element: <NETWORK_MANAGEMENT.CUSTOMER_OTP_CODES />
	},
	{
		path: '/compliance-security/otp-codes/agent',
		element: <NETWORK_MANAGEMENT.AGENT_OTP_CODES />
	},
	{
		path: '/reports/operations-reports',
		element: (
			<ProtectedRoute requiredPermission='reports.operations-reports'>	
				<REPORTS.REPORTS />,
			</ProtectedRoute>
		),
	},
	{
		path: '/reports/compliance-reports',
		element: (
			<ProtectedRoute requiredPermission='reports.compliance-reports'>
				<REPORTS.COMPLIANCE_REPORTS />,
			</ProtectedRoute>
		),
	},

	{
		path: '/financial-management/transfer-management',
		element: (
			<ProtectedRoute requiredPermission='financial-management.transfer-management'>
				<TRANSFER.TRANSFER_MANAGEMENT />,
			</ProtectedRoute>
		),
	},

	{
		path: '/financial-management/settlements/agent',
		element: (
			<ProtectedRoute requiredPermission='financial-management.agent-settlement'>
				<SETTLEMENT_MANAGEMENT.SETTLEMENTS />
			</ProtectedRoute>
		),
	},
	{
		path: '/financial-management/settlements/agent/transactions',
		element: (
			<ProtectedRoute requiredPermission='financial-management.agent-settlement'>
				<SETTLEMENT_MANAGEMENT.AGENT_TRANSACTIONS />
			</ProtectedRoute>
		),
	},

	// {
	// 	path: '/send-money/manage-payment',
	// 	element: <TRANSFER.MANAGE_PAYMENT />,
	// },
];
const contents = [...presentation, ...documentation];

export default contents;
